import { useCallback, useState, createContext, useContext, memo, useId, useMemo } from 'react';
import type { AccordionContextValue, OAccordionProps, AccordionItemId, AccordionItemIds } from './OAccordion.types';
import { Wrapper } from './OAccordion.styles';
import { MAccordionHeader } from '../../molecules/MAccordionHeader/index';
import { MAccordionItem } from '../../molecules/MAccordionItem/index';
import type { MAccordionControlProps } from '../../molecules/MAccordionControl/MAccordionControl.types';
import type { MAccordionHeaderProps } from '../../molecules/MAccordionHeader/MAccordionHeader.types';
import clsx from 'clsx';

const COMPONENT_NAME = 'o-mi-web-ui-kit-OAccordion';
const TEST_ID = 'mi-web-ui-kit-OAccordion';

// TODO: Get I18n key for this
const ACCORDION_EMPTY_TEXT = 'No accordion items available';

const AccordionContext = createContext<AccordionContextValue | null>(null);

function useAccordionContext() {
  const ctx = useContext(AccordionContext);
  if (!ctx) throw new Error('Accordion components must be used within <OAccordion>.');
  return ctx;
}

const OAccordion = memo(function OAccordion(props: OAccordionProps) {
  const {
    model,
    isAuthorMode,
    aemReactComponentMapping,
    expandedIds,
    defaultExpandedIds,
    onExpandedIdsChange,
    unmountOnCollapse = false,
    type: _unusedType,
    className,
    ...rest
  } = props;

  const { itemPath, pagePath, cqItems, cqItemsOrder = [], mAccordionItemMulti = [], type, mAccordionHeader } = model;

  const idBase = useId();
  const isControlled = expandedIds !== undefined;

  const [uncontrolledExpandedIds, setUncontrolledExpandedIds] = useState<AccordionItemIds>(() => {
    if (defaultExpandedIds !== undefined) return defaultExpandedIds;
    const allItemIds = mAccordionItemMulti.map(i => i.nodeName);
    return isAuthorMode ? allItemIds : [];
  });

  const currentExpandedIds = isControlled ? (expandedIds as AccordionItemIds) : uncontrolledExpandedIds;

  const setExpandedIds = useCallback(
    (next: AccordionItemIds) => {
      onExpandedIdsChange?.(next);
      if (!isControlled) setUncontrolledExpandedIds(next);
    },
    [isControlled, onExpandedIdsChange]
  );

  const [itemIds, setItemIds] = useState<AccordionItemId[]>([]);

  const registerItemId = useCallback((id: AccordionItemId) => {
    setItemIds(prev => (prev.includes(id) ? prev : [...prev, id]));
  }, []);

  const unregisterItemId = useCallback((id: AccordionItemId) => {
    setItemIds(prev => prev.filter(x => x !== id));
  }, []);

  const expandedSet = useMemo(() => {
    return new Set<AccordionItemId>(currentExpandedIds ?? []);
  }, [currentExpandedIds]);

  const ctx = useMemo<AccordionContextValue>(
    () => ({
      idBase,
      expandedSet,
      setExpandedIds,
      registerItemId,
      unregisterItemId,
      itemIds,
      unmountOnCollapse,
    }),
    [idBase, expandedSet, setExpandedIds, registerItemId, unregisterItemId, itemIds, unmountOnCollapse]
  );

  const _mAccordionHeaderProps = mAccordionHeader
    ? ({
        ...mAccordionHeader,
        aHeader: mAccordionHeader.aHeader
          ? {
              ...mAccordionHeader.aHeader,
              element: mAccordionHeader.aHeader?.headerTag?.toLowerCase(),
            }
          : undefined,
        link: mAccordionHeader.link === 'true',
        className: clsx(mAccordionHeader.className, 'pb-4'),
      } as MAccordionHeaderProps)
    : undefined;

  // All items from mAccordionItemMulti, ordered by cqItemsOrder first, then remaining items in their original order
  const itemsToRender = useMemo(() => {
    const orderedSet = new Set(cqItemsOrder);

    // Get items that are in cqItemsOrder, preserving cqItemsOrder's order
    const orderedItems = cqItemsOrder
      .map(itemKey => mAccordionItemMulti.find(el => el.nodeName === itemKey))
      .filter(Boolean);

    // Get remaining items not in cqItemsOrder, keeping their original order from mAccordionItemMulti
    const remainingItems = mAccordionItemMulti.filter(item => !orderedSet.has(item.nodeName));

    return [...orderedItems, ...remainingItems];
  }, [cqItemsOrder, mAccordionItemMulti]);

  return (
    <AccordionContext.Provider value={ctx}>
      <Wrapper
        data-component-name={COMPONENT_NAME}
        data-testid={TEST_ID}
        className={clsx('container px-0', className)}
        {...rest}
      >
        <div className="col-12 col-lg-10 px-0 d-flex flex-column">
          {_mAccordionHeaderProps && <MAccordionHeader {..._mAccordionHeaderProps} />}

          {/* Render accordion items */}
          {itemsToRender.length > 0 ? (
            itemsToRender.map(item => {
              const { mAccordionControl, nodeName } = item!;
              const _aImage = mAccordionControl?.aImage?.image
                ? {
                    ...mAccordionControl.aImage.image,
                    src: mAccordionControl.aImage.image.assetPath,
                    alt: mAccordionControl.aImage.image.altText,
                  }
                : undefined;
              const mAccordionControlProps = {
                ...mAccordionControl,
                aImage: _aImage,
                type,
              } as MAccordionControlProps;
              return (
                <MAccordionItem
                  id={nodeName}
                  key={nodeName}
                  mAccordionControl={mAccordionControlProps}
                  content={{
                    pagePath,
                    itemPath: `${itemPath}/${nodeName}`,
                    isAuthorMode,
                    cqItems: cqItems[nodeName]?.[':items'] || {},
                    model: { ...model, ':order': cqItems[nodeName]?.[':itemsOrder'] },
                    aemReactComponentMapping,
                  }}
                />
              );
            })
          ) : (
            <div>{ACCORDION_EMPTY_TEXT}</div>
          )}
        </div>
      </Wrapper>
    </AccordionContext.Provider>
  );
});

export { OAccordion, AccordionContext, useAccordionContext, TEST_ID as O_ACCORDION_TEST_ID };
